import { FiBell } from '@react-icons/all-files/fi/FiBell'
import { FiBellOff } from '@react-icons/all-files/fi/FiBellOff'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiSettings } from '@react-icons/all-files/fi/FiSettings'
import { IoNotifications } from '@react-icons/all-files/io5/IoNotifications'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useFollowTrackMutation,
  useGetTrackFollowQuery,
} from '@disco/state-management/api/discussions'
import { useGetNotesNotificationPreferenceQuery } from '@disco/state-management/api/notification-preferences'
import { pushUINotification } from '@disco/utils/notifications'

import type { TrackFollowLevel } from 'types/discussion'
import type { TrackId } from 'types/track'

import {
  AccessibleIcon,
  Box,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'

type IconComponent = React.ComponentType<{ size?: number }>

const LEVEL_OPTIONS: {
  value: TrackFollowLevel
  label: string
  Icon: IconComponent
}[] = [
  { value: 'all', label: 'All notes', Icon: FiBell },
  {
    value: 'mentions_and_replies',
    label: 'Mentions and replies only',
    Icon: FiBell,
  },
  { value: 'off', label: 'Off', Icon: FiBellOff },
]

const ON_TOOLTIP = 'On (all notes or mention & replies)'
const OFF_TOOLTIP = 'Off'

type Props = {
  trackId: TrackId
}

const TrackFollowBell = ({ trackId }: Props): React.ReactElement => {
  const { data } = useGetTrackFollowQuery(trackId, {
    refetchOnMountOrArgChange: true,
  })
  const { data: globalPreference } = useGetNotesNotificationPreferenceQuery()
  const [level, setLevel] = useState<TrackFollowLevel | null>(null)
  const [followTrack] = useFollowTrackMutation()
  const navigate = useNavigate()

  const seededRef = useRef(false)
  useEffect(() => {
    if (data && !seededRef.current) {
      seededRef.current = true
      setLevel(data.level)
    }
  }, [data])

  const handleSelect = async (next: TrackFollowLevel): Promise<void> => {
    seededRef.current = true
    const previous = level
    setLevel(next)
    try {
      await followTrack({ track_id: trackId, level: next }).unwrap()
    } catch {
      setLevel(current => (current === next ? previous : current))
      pushUINotification({
        message:
          'Could not update your notification setting. Please try again.',
        level: 'error',
      })
    }
  }

  const effectiveLevel = level ?? globalPreference?.activity_level ?? 'all'
  const isOff = effectiveLevel === 'off'
  const TriggerIcon = isOff ? FiBellOff : IoNotifications

  return (
    <DropdownMenu.Root>
      <Tooltip content={isOff ? OFF_TOOLTIP : ON_TOOLTIP}>
        <DropdownMenu.Trigger>
          <IconButton
            size="2"
            variant="ghost"
            color="gray"
            aria-label={
              isOff
                ? 'Notifications off for this track'
                : 'Notifications on for this track'
            }
          >
            <AccessibleIcon label="Track notifications">
              <TriggerIcon size={20} />
            </AccessibleIcon>
          </IconButton>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Content align="end">
        {LEVEL_OPTIONS.map(({ value, label, Icon }) => (
          <DropdownMenu.Item key={value} onSelect={() => handleSelect(value)}>
            <Flex align="center" gap="2" width="100%">
              <Icon size={16} />
              <Text size="2">{label}</Text>
              {effectiveLevel === value && (
                <Box ml="auto" data-testid={`follow-level-current-${value}`}>
                  <FiCheck size={16} />
                </Box>
              )}
            </Flex>
          </DropdownMenu.Item>
        ))}
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={() => navigate('/settings/notifications')}>
          <Flex align="center" gap="2">
            <FiSettings size={16} />
            <Text size="2">Manage all notifications</Text>
          </Flex>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default TrackFollowBell
